import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { UnlimitedTaskCap } from "client/api/CloudSubscriptionApi";
import { ErrorAlert } from "components/alert/ErrorAlert";

type ChangeTaskCapDialogProps = {
  cloudSubscriptionId: string;
  open: boolean;
  currentTaskCap: string | undefined;
  handleClose: () => void;
  submitChange: (taskCapSelection: string, applyDuringOutageWindow: boolean) => Promise<void>;
};

export function ChangeTaskCapDialog(props: ChangeTaskCapDialogProps) {
  const taskCapOptions = ["5", "10", "20", "40", "80", "160", "320", UnlimitedTaskCap];
  const [taskCapSelection, setTaskCapSelection] = useState<string>(props.currentTaskCap || "5");
  const [applyDuringOutageWindow, setApplyDuringOutageWindow] = useState<boolean>(true);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | undefined>();

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!loading) props.handleClose();
      }}
    >
      {error ? <ErrorAlert error={error} /> : null}
      <DialogTitle>Change Task Cap</DialogTitle>
      <DialogContent>
        <Box>
          <h4>This feature is only intended to be used by the Sales team.</h4>
          <div>
            <i>
              This is because changing the task cap has a potentially significant impact on cost. If you need to change
              the task cap for a customer, please confirm with Sales first. If you are changing the task cap to 40 or
              higher, please inform{" "}
              <Link href={"https://octopusdeploy.slack.com/archives/C01Q95KPRM4"} target="_blank">
                #cloud-platform-requests
              </Link>
            </i>
          </div>
          <div>
            <FormControl sx={{ marginTop: 4 }} fullWidth>
              <InputLabel id="task-cap-select-label">Task Cap</InputLabel>
              <Select
                labelId={"task-cap-select-label"}
                label={"Task Cap"}
                onChange={(event) => setTaskCapSelection(event.target.value)}
                value={taskCapSelection}
              >
                {Object.values(taskCapOptions).map((value) => (
                  <MenuItem value={value} key={value}>
                    {value === UnlimitedTaskCap ? "Unlimited" : value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  onChange={(e) => setApplyDuringOutageWindow(e.target.checked)}
                  checked={applyDuringOutageWindow}
                />
              }
              label={"Apply during instance maintenance window"}
            />
            {!applyDuringOutageWindow && (
              <Grid size={12}>
                <Alert severity="warning">
                  This operation may cause an outage for the instance while resources are modified. Running outside the
                  maintenance window should only be done with the agreement of the customer.
                </Alert>
              </Grid>
            )}
            <Button
              disabled={loading}
              sx={{ marginTop: 4, float: "right" }}
              variant="contained"
              onClick={async () => {
                setLoading(true);
                try {
                  await props.submitChange(taskCapSelection, applyDuringOutageWindow);
                } catch (e) {
                  setError(e);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? "Updating Task Cap..." : "Update Task Cap"}
            </Button>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
